<template>
  <div class="login content">
    <header></header>
    <div class="login-wrapper">
      <div class="login-content" v-if="isLoginState">
        <h2 class="mb-4 text-center text-capitalize">
          {{ this.$t("Sign in") }}
        </h2>
        <form @submit.prevent class="w-100" autocomplete="off">
          <b-form-group
            :label="$t('Email')"
            class="display-block mb-4"
            label-for="email"
            :state="$validateState($v.form.email)"
          >
            <b-form-input
              v-focus-input
              data-test-id="login-email"
              :state="$validateState($v.form.email)"
              id="email"
              v-model="$v.form.email.$model"
              @blur="setValidationInput('email', $event.target.value)"
              @keyup="
                cleanErrorMessage;
                removeWhitespaces('email', $event.target.value);
              "
            />
            <template #invalid-feedback>
              <p v-if="!$v.form.email.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
          <b-form-group
            :label="$t('Password')"
            class="mb-4"
            label-for="password"
            :state="$validateState($v.form.password)"
          >
            <b-form-input
              type="password"
              data-test-id="login-password"
              :state="$validateState($v.form.password)"
              id="password"
              v-model="$v.form.password.$model"
              @keyup="cleanErrorMessage"
            />
            <template #invalid-feedback>
              <p v-if="!$v.form.password.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
          <p v-if="authError" class="text-center text-danger">
            {{
              $t(
                "The email and password you entered did not match our records. Please try again."
              )
            }}
          </p>
          <div class="forgot mb-4">
            <a
              class="link"
              @click="setResetPasswordState"
              href="#"
              data-test-id="login-forgot-password"
              >{{ this.$t("Forgot your password?") }}</a
            >
          </div>
          <div class="auth text-center">
            <b-button @click="submit" type="submit" class="mx-auto mb-3">
              <span v-if="!fetching"> {{ this.$t("Continue") }} </span>
              <span class="px-2" v-else>
                <b-icon icon="arrow-clockwise" animation="spin"
              /></span>
            </b-button>
          </div>
        </form>
      </div>
      <div class="login-content two-factor" v-if="isTwoFAState">
        <h2 class="mb-4 text-center text-capitalize">
          {{ this.$t("Two-factor authentication") }}
        </h2>
        <form @submit.prevent="submit" class="w-100" autocomplete="off">
          <p>
            {{
              $t(
                "To make sure it's really you, please enter the verification code generated by the authenticator app on your mobile device."
              )
            }}
          </p>
          <b-form-group class="display-block align-center" label-for="code">
            <div class="two-factor-code">
              <div v-if="!showBackupCode" class="code-block">
                <b-form-input
                  data-test-id="login-2fa-code1"
                  class="two-factor-digit"
                  id="code1"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="1"
                  autocomplete="false"
                  v-model="code1"
                  autofocus
                  @keyup="input2fa"
                  @paste="onPaste"
                />
                <b-form-input
                  data-test-id="login-2fa-code2"
                  class="two-factor-digit"
                  id="code2"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="2"
                  autocomplete="false"
                  v-model="code2"
                  @keyup="input2fa"
                  @paste="onPaste"
                />
                <b-form-input
                  data-test-id="login-2fa-code3"
                  class="two-factor-digit"
                  id="code3"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="3"
                  autocomplete="false"
                  v-model="code3"
                  @keyup="input2fa"
                  @paste="onPaste"
                />
                <b-form-input
                  data-test-id="login-2fa-code4"
                  class="two-factor-digit middle-separator"
                  id="code4"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="4"
                  autocomplete="false"
                  v-model="code4"
                  @keyup="input2fa"
                  @paste="onPaste"
                />
                <b-form-input
                  data-test-id="login-2fa-code5"
                  class="two-factor-digit"
                  id="code5"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="5"
                  autocomplete="false"
                  v-model="code5"
                  @keyup="input2fa"
                  @paste="onPaste"
                />
                <b-form-input
                  data-test-id="login-2fa-code6"
                  class="two-factor-digit"
                  id="code6"
                  maxlength="1"
                  pattern="[\d]*"
                  tabindex="6"
                  autocomplete="false"
                  v-model="code6"
                  ref="code6"
                  @keyup="input2fa"
                  @paste="onPaste"
                />
              </div>
              <div v-if="showBackupCode" class="backup-code-block">
                <b-form-group
                  class="display-block"
                  label-for="backupCode"
                  :label="$t('Backup Code')"
                >
                  <b-form-input
                    data-test-id="login-2fa-backup-code"
                    class="two-factor-digit"
                    id="backupCode"
                    maxlength="8"
                    pattern="[\d]*"
                    tabindex="1"
                    autocomplete="false"
                    v-model="backupCode"
                    @keyup="inputBackupCode"
                    ref="backupCode1"
                  />
                </b-form-group>
              </div>
            </div>
            <template #invalid-feedback>
              <p v-if="!$v.form.code.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>

          <p v-if="authError2fa" class="text-center text-danger">
            {{ $t("The code you entered is not valid. Please try again.") }}
          </p>
          <div class="auth text-center">
            <p class="text-center">
              {{ $t("Not working?") }}
              <a
                class="link"
                href="#"
                data-test-id="2fa-try-again"
                @click="cancel2fa"
                >{{ this.$t("Try again") }}</a
              >
            </p>
            <p v-if="isTwoFAState && !showBackupCode">
              {{ $t("Or") }},
              <a
                class="link"
                href="#"
                data-test-id="2fa-try-another-way"
                @click="enableBackupCode"
                >{{ this.$t("try another way to log in.") }}</a
              >
            </p>
          </div>
          <div class="auth text-center">
            <b-button type="submit" class="mx-auto mb-3">
              <span v-if="!fetching">
                {{ this.$t("Sign in") }}
              </span>
              <span class="px-2" v-else
                ><b-icon icon="arrow-clockwise" animation="spin"
              /></span>
            </b-button>
          </div>
        </form>
      </div>
      <div class="login-content" v-if="isTwoFASetupState">
        <two-fa-setup
          :email="form.email"
          :password="form.password"
          @cancel="setLoginState"
          @success="doTheMagic"
          @login="onLoginClick"
        />
      </div>
      <div class="login-content forgot-password" v-if="isResetPasswordState">
        <h2
          v-if="forgotPasswordSuccess"
          class="mb-4 text-center text-capitalize"
        >
          {{ this.$t("Password reset") }}
        </h2>
        <h2 v-else class="mb-4 text-center text-capitalize">
          {{ this.$t("Forgot password") }}
        </h2>
        <form @submit.prevent="submitForgotPasswordForm" class="w-100">
          <p v-if="forgotPasswordSuccess">
            {{
              $t(
                "Please check your inbox for your reset password email. If it does not appear within a few minutes, please check your spam folder."
              )
            }}
          </p>
          <p v-else>
            {{
              $t(
                "Enter the email address associated with your account, and we'll send you a new password."
              )
            }}
          </p>
          <b-form-group
            :label="$t('Email')"
            class="display-block"
            label-for="email"
            :state="$validateState($v.forgotPasswordForm.email)"
          >
            <b-form-input
              data-test-id="login-email"
              :state="$validateState($v.forgotPasswordForm.email)"
              :disabled="fetching"
              id="email"
              v-model="$v.forgotPasswordForm.email.$model"
              @blur="setValidationInput('email', $event.target.value)"
              @keyup="cleanErrorMessage"
            />
            <template #invalid-feedback>
              <p v-if="!$v.forgotPasswordForm.email.required">
                {{ $t("This is a required field.") }}
              </p>
              <p
                v-if="
                  !$v.forgotPasswordForm.email.email &&
                  $v.forgotPasswordForm.email.required
                "
              >
                {{ $t("Please enter a valid email") }}
              </p>
            </template>
          </b-form-group>
          <p v-if="forgotPasswordError" class="text-center text-danger">
            {{
              $t(
                "Something went wrong requesting password reset. Please try again."
              )
            }}
          </p>
          <div class="auth text-center">
            <b-button type="submit" class="mx-auto mb-3">
              <span v-if="!fetching"> {{ this.$t("Continue") }} </span>
              <span class="px-2" v-else
                ><b-icon icon="arrow-clockwise" animation="spin"
              /></span>
            </b-button>
            <div class="forgot mb-2">
              <a
                class="link"
                @click="setLoginState"
                href="#"
                data-test-id="login-forgot-password"
                >{{ this.$t("Return to sign in") }}</a
              >
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { required, numeric, email } from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";
import TwoFaSetup from "@/pages/login/TwoFaSetup";

const LOGIN_STATE = "login";
const TWO_FA_STATE = "twoFA";
const TWO_FA_SETUP_STATE = "twoFASetup";
const RESET_PASSWORD_STATE = "resetPassword";

export default {
  layout: "blank",
  components: {
    TwoFaSetup,
  },
  data() {
    return {
      state: LOGIN_STATE,
      fetching: false,
      authError: false,
      authError2fa: false,
      forgotPasswordError: false,
      forgotPasswordSuccess: false,
      showBackupCode: false,
      form: {
        email: null,
        password: null,
        code: null,
      },
      forgotPasswordForm: {
        email: null,
      },
      code1: null,
      code2: null,
      code3: null,
      code4: null,
      code5: null,
      code6: null,
      backupCode: null,
    };
  },
  validations: {
    form: {
      email: {
        required,
      },
      password: {
        required,
      },
      code: {
        numeric,
      },
    },
    forgotPasswordForm: {
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    ...mapGetters("authentication", ["token", "role", "name", "email"]),
    ...mapGetters("userInterface", ["logo", "title"]),
    isLoginState() {
      return this.state === LOGIN_STATE;
    },
    isTwoFAState() {
      return this.state === TWO_FA_STATE;
    },
    isTwoFASetupState() {
      return this.state === TWO_FA_SETUP_STATE;
    },
    isResetPasswordState() {
      return this.state === RESET_PASSWORD_STATE;
    },
  },
  methods: {
    ...mapActions("authentication", [
      "requestToken",
      "setRole",
      "getAuthUser",
      "forgotPassword",
    ]),
    ...mapActions("toast", ["displayToast"]),
    ...mapMutations("authentication", ["setBackupCodes"]),

    routeBasedOnRole() {
      const userToken = this.$userPrefs.getCurrentToken();
      if (userToken) {
        this.fetchInterface({ mspId: null });
        const userRole = this.$userPrefs.getUserRole();
        switch (userRole) {
          case 1:
            this.$router.push("/system/overview");
            break;
          case 2:
            this.$router.push("/sales/msps");
            break;
          case 3:
            this.$router.push("/msp/overview");
            break;
          case 4:
            this.$router.push("/customer/overview");
            break;
        }
      }
    },
    ...mapActions("authentication", ["requestToken"]),
    ...mapActions("userInterface", ["fetchInterface"]),
    setValidationInput(object, value) {
      if (this.isResetPasswordState) {
        this.forgotPasswordForm[`${object}`] = value;
        this.$v.forgotPasswordForm[`${object}`].$touch();
      } else {
        this.form[`${object}`] = value;
        this.$v.form[`${object}`].$touch();
      }
    },
    cleanErrorMessage() {
      this.authError = false;
      this.authError2fa = false;
      this.forgotPasswordError = false;
    },
    input2fa(e) {
      this.authError2fa = false;
      this.fetching = false;
      let code = e.keyCode || e.which;
      if (code === 8 || code === 37) {
        if (e.target.id !== "code1") {
          e.target.previousSibling.select();
        }
      } else {
        if (e.target.id !== "code6") {
          e.target.nextSibling.select();
        }
      }
      this.consolidate2faCode();
    },
    inputBackupCode() {
      this.form.code = this.backupCode;
      this.authError2fa = false;
    },
    enableBackupCode() {
      this.showBackupCode = true;
      this.authError2fa = false;
    },
    setLoginState() {
      this.state = LOGIN_STATE;
    },
    setResetPasswordState() {
      this.state = RESET_PASSWORD_STATE;

      if (this.forgotPasswordSuccess === true) {
        this.forgotPasswordSuccess = false;
      }
      this.$v.forgotPasswordForm.$reset();
    },
    consolidate2faCode() {
      this.form.code =
        this.code1 +
        this.code2 +
        this.code3 +
        this.code4 +
        this.code5 +
        this.code6;
    },
    cancel2fa() {
      this.authError = false;
      this.authError2fa = false;
      this.showBackupCode = false;
      this.state = LOGIN_STATE;
      this.form.code = null;
      this.backupCode = "";
      this.code1 = "";
      this.code2 = "";
      this.code3 = "";
      this.code4 = "";
      this.code5 = "";
      this.code6 = "";
    },

    onPaste(ev) {
      const clip = ev.clipboardData.getData("text").trim();
      if (!/\d{6}/.test(clip)) return ev.preventDefault(); // Invalid. Exit here
      const s = [...clip];
      this.code1 = s[0];
      this.code2 = s[1];
      this.code3 = s[2];
      this.code4 = s[3];
      this.code5 = s[4];
      this.code6 = s[5];
    },
    removeWhitespaces(object, value) {
      this.form[`${object}`] = value.replace(/\s+/g, "");
      this.$v.form[`${object}`].$touch();
    },
    async submitForgotPasswordForm() {
      this.$v.forgotPasswordForm.$touch();
      if (!this.$v.forgotPasswordForm.$invalid && !this.fetching) {
        try {
          this.fetching = true;
          const res = await this.forgotPassword({
            email: this.forgotPasswordForm.email,
          });
          if (res.code === 200) {
            this.forgotPasswordSuccess = true;
            this.$v.forgotPasswordForm.email.$touch();
            this.$v.forgotPasswordForm.$reset();
            this.forgotPasswordForm.email = null;
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("Password reset link sent"),
              duration: 2000,
              variant: "success",
            });
          } else {
            this.displayToast({
              title: this.$t("Error"),
              message: this.$t("Something went wrong."),
              duration: 2000,
              variant: "danger",
            });
            this.forgotPasswordError = true;
          }
        } catch (err) {
          console.error(err.message); // eslint-disable-line no-console
        }

        this.fetching = false;
      }
    },
    async doTheMagic() {
      const splitToken = atob(this.token.access_token.split(".")[1]);
      const parsedToken = JSON.parse(splitToken);

      await this.fetchInterface({ mspId: null });

      this.$userPrefs.updateUserPrefs({
        token: this.token.access_token,
        role: this.role,
        twoFactorAuthEnabled: parsedToken["2fa_enabled"],
        ui: {
          logo: this.$userPrefs.getCurrentUi().logo,
          title: this.$userPrefs.getCurrentUi().title,
        },
      });

      await this.getAuthUser({ id: +parsedToken.sub });
    },
    async submit() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid && !this.fetching) {
        try {
          this.fetching = true;
          await this.requestToken({
            username: this.form.email,
            password: this.form.password,
            code: this.form.code,
          });

          await this.doTheMagic();

          this.routeBasedOnRole();
        } catch (err) {
          this.fetching = false;

          if (
            err.response.status === 400 &&
            (err.response.data.error.includes("2FA is enabled") ||
              err.response.data.error.includes("Code required"))
          ) {
            this.state = TWO_FA_STATE;
            return;
          }
          if (
            err.response.status === 400 &&
            err.response.data.error.includes("2FA is enforced")
          ) {
            this.state = TWO_FA_SETUP_STATE;
            return;
          }
          if (
            err.response.status === 422 &&
            err.response.data.error.includes("Incorrect code entered")
          ) {
            this.authError2fa = true;
            return;
          }
          if (err.response.status === 401) {
            this.authError = true;
            return;
          }
        }
      }
    },
    onLoginClick() {
      this.routeBasedOnRole();
      this.setBackupCodes(null);
    },
  },
  beforeMount() {
    const token = this.$userPrefs.getCurrentToken();
    if (token) {
      const splitToken = atob(token.split(".")[1]);
      const parsedToken = JSON.parse(splitToken);
      if (+parsedToken.exp * 1000 > Date.now()) {
        this.routeBasedOnRole();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.two-factor {
  min-width: 580px;

  .message {
    margin-bottom: 3rem;
  }
}

.two-factor-code {
  display: flex;
  width: 230px;
  text-align: center;
  margin: auto;

  input {
    height: 50px;
    font-size: 24px;
    padding: 5px;
    text-align: center;
  }

  .two-factor-digit {
    font-weight: bold;
  }

  .middle-separator {
    margin-left: 25px;
  }
}

.code-block {
  text-align: center;
  display: flex;
  margin-left: auto;
  margin-right: auto;
}

.backup-code-block {
  text-align: center;
  display: flex;
  margin-left: auto;
  margin-right: auto;
}

.login {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;

  header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;
    padding: 35px 35px 0;

    .cta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      p {
        margin: 0 24px 0 0;
      }

      .cta-link {
        border: 2px solid $secondary;
        padding: 16px 24px;
        color: $secondary;
        border-radius: 4px;
        font-weight: 700;
        transition: all 0.3s ease-in-out;

        &:hover {
          background-color: $secondary;
          color: $white;
          text-decoration: none;
        }
      }
    }
  }

  a.link:link,
  a.link:active,
  a.link:visited {
    color: $body-color;
    text-decoration: none;

    &:hover,
    &:focus {
      color: $secondary;
    }
  }

  &-wrapper {
    height: 100vh;
  }

  &-content {
    width: 400px;
    max-width: 95%;
    position: relative;
    top: 50%;
    left: 50%;
    padding: 20px;
    flex-direction: column;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    transform: translateY(-50%) translateX(-50%);
    border-radius: 5px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);

    form {
      .forgot {
        display: flex;
        justify-content: center;
      }

      .auth {
        max-width: 312px;
        margin: 0 auto;

        p {
          width: 100%;
          text-align: center;
          font-size: 14px;
        }

        &::v-deep a {
          color: $secondary;
          text-decoration: none;
        }
      }
    }
  }
}
</style>
